import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import TextAreaInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextAreaInput';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import Typeahead from '@oncolens/oncolens-ui/lib/components/FormControls/Typeahead/Typeahead';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { RiskFactorForCreationDto, RiskFactorForUpdateDto } from '../dtos/RiskFactor';
import { useDefinitionConfig } from '../hooks/useCancerConfig';
import { riskFactorSchema } from './schema';
import { RiskFactorItem } from './RiskFactors';

interface RiskFactorFormProps {
	data: RiskFactorItem;
	previouslySaved: boolean;
	isInAutosave: boolean;
	isApiActionExecuting: boolean;
	onCancel: () => void;
	onUpdate: (formValues: any) => void;
	onApiCreate: (formValues: RiskFactorForCreationDto) => void;
	onApiUpdate: (formValues: RiskFactorForUpdateDto) => void;
}

export const RiskFactorForm = ({
  data,
  previouslySaved,
  isInAutosave,
  isApiActionExecuting,
  onCancel,
  onUpdate,
  onApiCreate,
  onApiUpdate
}: RiskFactorFormProps) => {
  const {
    getValues,
    trigger,
    reset,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(riskFactorSchema)
  });

  useEffect(() => {
    reset(data);
  }, []);

  const validateCreateUpdate = (createUpdateAction: () => void) => {
    void trigger().then((isValid) => {
      if (isValid) {
        createUpdateAction();
      }
    });
  };

  const handleCancel = () => {
    onCancel();
    reset(data);
  };

  const handleUpdate = () => {
    validateCreateUpdate(() => {
      onUpdate(getValues());
    });
  };

  const handleApiUpdate = () => {
    validateCreateUpdate(() => {
      onApiUpdate(getValues() as RiskFactorForUpdateDto);
    });
  };

  const handleApiCreate = () => {
    validateCreateUpdate(() => {
      onApiCreate(getValues() as RiskFactorForCreationDto);
    });
  };

  const { data: definitionConfigDataSource } = useDefinitionConfig('Risk Factor');

  const definitionConfigData = definitionConfigDataSource?.map((item) => ({
    label: item.label,
    value: item.label
  }));

  return (
    <div data-cy="risk-factor-form" className="grid grid-cols-5 col-span-5 gap-3">
      <div className="col-span-5">
        <h2 className="pb-0.5 pr-1 inline-block text-base font-semibold border-b-2 border-oncolensBlue-400">
          {previouslySaved ? 'Update Risk Factor' : 'Add New Risk Factor'}
        </h2>
      </div>

      <div className="col-span-5 md:col-span-2">
        <Controller
          name={'description'}
          control={control}
          render={({ field }) => (
            <Typeahead
              id="risk-factors-description-typeahead"
              data={definitionConfigData || []}
              label="Description"
              required={true}
              error={errors?.description?.message}
              selected={field.value ? { label: field.value, value: field.value } : null}
              onChange={(item) => {
                field.onChange(item ? item.value : null);
              }}
              className="col-span-3"
              placeholder="Start typing a description..."
              options={{ allowCustomInput: true }}
              sortingOptions={{ sortable: true, sortProperty: 'label' }}
            />
          )}
        />
      </div>
      <Controller
        name="value"
        control={control}
        render={({ field }) => (
          <TextInput
            id="value"
            label="Value"
            type="text"
            htmlName="value"
            value={field.value}
            onChange={field.onChange}
            wrapperProps={{ className: 'col-span-5 md:col-span-1' }}
          />
        )}
      />

      <Controller
        name="comments"
        control={control}
        render={({ field }) => (
          <TextAreaInput
            id="comments"
            label="Comments"
            rows={1}
            resize="vertical"
            htmlName="comments"
            placeholder="Enter comments"
            value={field.value}
            onChange={field.onChange}
            wrapperProps={{ className: 'col-span-5 md:col-span-2' }}
            testSelector="textarea-input-risk-factor-comments"
          />
        )}
      />

      <div className="flex col-span-5 my-1 ">
        <div className="flex-1 block mr-2 sm:flex-initial sm:inline-block sm:ml-auto">
          <Button
            testSelector="button-risk-factor-cancel"
            buttonStyle="secondary"
            onClick={handleCancel}
            buttonColor="blue"
            type="button"
            className="inline-block rounded sm:py-1"
          >
						Cancel
          </Button>
        </div>
        <div className="flex-1 block sm:flex-initial sm:inline-block">
          {isInAutosave ? (
            <ButtonWithLoading
              buttonStyle="primary"
              onClick={previouslySaved ? handleApiUpdate : handleApiCreate}
              loading={isApiActionExecuting}
              buttonColor="blue"
              className="inline-block rounded sm:px-4 sm:py-1"
              testSelector="button-risk-factor-add"
            >
              {previouslySaved ? 'Update' : 'Add'}
            </ButtonWithLoading>
          ) : (
            <Button
              buttonStyle="primary"
              buttonColor="blue"
              onClick={handleUpdate}
              className="inline-block rounded sm:px-4 sm:py-1"
            >
              {previouslySaved ? 'Update' : 'Add'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export { RiskFactorForm as default };
